import { StrategyBuilderFunction, EMPTY_STRATEGY } from './types';
import { addLongPut, addShortPut } from './addOption';
import {
  getDefaultExpiration,
  findClosestStrikePrice,
  getStrikesAtExpiration,
  expirationToDate,
  getPremium,
  getStrategyLegStrikeDirection,
  getStrikeDistance,
} from './utils';

export const putRatioSpread: StrategyBuilderFunction = ({
  chain,
  spot,
  expirationDate,
  expirationKey,
  strikeDirection,
  contractMultiplier = 1,
}) => {
  const strikesAtExpiration = expirationKey
    ? getStrikesAtExpiration(chain, expirationKey)
    : getDefaultExpiration(chain);

  if (
    !strikesAtExpiration ||
    !strikesAtExpiration.e ||
    !strikesAtExpiration.ey ||
    !strikesAtExpiration.em ||
    !strikesAtExpiration.ed
  ) {
    return EMPTY_STRATEGY;
  }

  const expirationDateForOptions =
    expirationDate ||
    expirationToDate(strikesAtExpiration.ey, strikesAtExpiration.em, strikesAtExpiration.ed);
  const expirationKeyForOptions = expirationKey || strikesAtExpiration.e;

  // Selecting strike prices for the strategy
  const lowerPutStrike = findClosestStrikePrice(
    strikesAtExpiration,
    getStrikeDistance({ price: spot, unit: -2 }),
  ); // Selling more puts
  const higherPutStrike = findClosestStrikePrice(strikesAtExpiration, spot); // Buying puts

  // The ratio typically involves selling more puts than are bought, e.g., 1:2 or 1:3 ratio
  const buyContracts = 1 * contractMultiplier;
  const sellContracts = 2 * contractMultiplier; // Adjust based on desired exposure and strategy specifics

  return {
    optionPositions: [
      // Buy puts at the higher strike
      addLongPut({
        strike: higherPutStrike,
        premium: getPremium({
          ask: strikesAtExpiration?.c?.[higherPutStrike]?.p?.a || 0,
          bid: strikesAtExpiration?.c?.[higherPutStrike]?.p?.b || 0,
          strikeDirection: getStrategyLegStrikeDirection(strikeDirection, 'long'),
        }),
        expirationDate: expirationDateForOptions,
        expirationKey: expirationKeyForOptions,
        numberOfContracts: buyContracts,
      }),
      // Sell more puts at the lower strike
      addShortPut({
        strike: lowerPutStrike,
        premium: getPremium({
          ask: strikesAtExpiration?.c?.[lowerPutStrike]?.p?.a || 0,
          bid: strikesAtExpiration?.c?.[lowerPutStrike]?.p?.b || 0,
          strikeDirection: getStrategyLegStrikeDirection(strikeDirection, 'short'),
        }),
        expirationDate: expirationDateForOptions,
        expirationKey: expirationKeyForOptions,
        numberOfContracts: sellContracts,
      }),
    ],
    stockPositions: [],
  };
};
